import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { IoChevronForward, IoChevronBack } from 'react-icons/io5';
import { getNotices, getNoticeCount, getNoticeDetail } from '../../../services/noticeService';

const PER_PAGE = 5;

const Container = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing.lg};
`;

const List = styled.div``;

const NoticeItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.base} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  cursor: pointer;
`;

const NoticeTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.colors.text};
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin-top: ${({ theme }) => theme.spacing.xl};
`;

const PageBtn = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: #fff;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: pointer;
  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
`;

const PageNum = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ $active, theme }) => $active ? theme.colors.primary : '#fff'};
  color: ${({ $active }) => $active ? '#fff' : '#6B7280'};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  cursor: pointer;
`;

const EmptyText = styled.p`
  text-align: center;
  padding: 60px 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* 상세 */
const DetailDate = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const DetailMeta = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const DetailTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const DetailBody = styled.div`
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.7;
  white-space: pre-line;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const ListBtn = styled.button`
  width: 100%;
  height: 48px;
  border: none;
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  position: fixed;
  bottom: 80px;
  left: ${({ theme }) => theme.realtor.pagePadding};
  right: ${({ theme }) => theme.realtor.pagePadding};
  width: auto;
`;

const RealtorNotice = () => {
  const location = useLocation();
  const [notices, setNotices] = useState([]);
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  // 메인에서 특정 공지 클릭해서 들어온 경우 바로 상세 표시
  useEffect(() => {
    const noticeId = location.state?.noticeId;
    if (noticeId) {
      getNoticeDetail(noticeId).then((n) => { if (n) setSelected(n); }).catch(() => {});
    }
  }, [location.state]);

  useEffect(() => {
    getNoticeCount().then((cnt) => setTotalPages(Math.max(1, Math.ceil(cnt / PER_PAGE)))).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    getNotices(page, PER_PAGE)
      .then(setNotices)
      .catch(() => setNotices([]))
      .finally(() => setLoading(false));
  }, [page]);

  if (selected) {
    return (
      <Container>
        <DetailDate>{selected.date}</DetailDate>
        <DetailMeta>{selected.createdBy || '관리자'} · {selected.target || '공지'}</DetailMeta>
        <DetailTitle>[공지] {selected.title}</DetailTitle>
        <DetailBody>{selected.content || selected.body || ''}</DetailBody>
        <ListBtn onClick={() => setSelected(null)}>목록보기</ListBtn>
      </Container>
    );
  }

  return (
    <Container>
      {loading ? (
        <EmptyText>로딩 중...</EmptyText>
      ) : notices.length === 0 ? (
        <EmptyText>공지사항이 없습니다.</EmptyText>
      ) : (
        <List>
          {notices.map((n) => (
            <NoticeItem key={n.id} onClick={() => setSelected(n)}>
              <NoticeTitle>{n.title}</NoticeTitle>
              <IoChevronForward size={16} color="#9CA3AF" />
            </NoticeItem>
          ))}
        </List>
      )}

      <Pagination>
        <PageBtn disabled={page === 1} onClick={() => setPage(1)}>«</PageBtn>
        <PageBtn disabled={page === 1} onClick={() => setPage(page - 1)}>
          <IoChevronBack size={14} />
        </PageBtn>
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
          <PageNum key={p} $active={page === p} onClick={() => setPage(p)}>
            {p}
          </PageNum>
        ))}
        <PageBtn disabled={page === totalPages} onClick={() => setPage(page + 1)}>
          <IoChevronForward size={14} />
        </PageBtn>
        <PageBtn disabled={page === totalPages} onClick={() => setPage(totalPages)}>»</PageBtn>
      </Pagination>
    </Container>
  );
};

export default RealtorNotice;
